import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  BusinessServiceItem,
  BusinessServicesResponse,
  CreateServiceRequest,
  UpdateServiceRequest,
} from "@/api/types";
import { businessSlugFromOverview, useDashboardRequest } from "./dashboard-request";

function invalidateServiceCatalogQueries(queryClient: QueryClient, slug: string | undefined) {
  void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  if (slug) {
    void queryClient.invalidateQueries({ queryKey: ["businesses", slug, "services"] });
    void queryClient.invalidateQueries({ queryKey: ["businesses", slug, "team"] });
    void queryClient.invalidateQueries({ queryKey: ["businesses", slug, "availability"] });
  }
}

export function useDashboardServices() {
  const dashboard = useDashboardRequest();

  return useQuery({
    queryKey: dashboard.queryKey("services"),
    queryFn: () =>
      api.get("dashboard/services", dashboard.requestOptions()).json<BusinessServicesResponse>(),
    staleTime: 60_000,
  });
}

export function useCreateDashboardService() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (data: CreateServiceRequest) =>
      api
        .post("dashboard/services", { ...dashboard.requestOptions(), json: data })
        .json<BusinessServiceItem>(),
    onSuccess: () => {
      invalidateServiceCatalogQueries(
        queryClient,
        businessSlugFromOverview(queryClient, dashboard.slug),
      );
    },
  });
}

export function useUpdateDashboardService() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateServiceRequest) =>
      api
        .patch(`dashboard/services/${id}`, { ...dashboard.requestOptions(), json: data })
        .json<BusinessServiceItem>(),
    onSuccess: () => {
      invalidateServiceCatalogQueries(
        queryClient,
        businessSlugFromOverview(queryClient, dashboard.slug),
      );
    },
  });
}

export function useDeactivateDashboardService() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (id: string) =>
      api
        .delete(`dashboard/services/${id}`, dashboard.requestOptions())
        .json<BusinessServiceItem>(),
    onSuccess: () => {
      invalidateServiceCatalogQueries(
        queryClient,
        businessSlugFromOverview(queryClient, dashboard.slug),
      );
    },
  });
}
